import {AsyncPipe} from "@angular/common";
import {Component, inject, OnInit, ViewChild} from "@angular/core";
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatAutocompleteModule} from "@angular/material/autocomplete";
import {MatButtonModule} from "@angular/material/button";
import {MatCardModule} from "@angular/material/card";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {MatInputModule} from "@angular/material/input";
import {MatRadioModule} from "@angular/material/radio";
import {MatSelectModule} from "@angular/material/select";
import {MatStepper, MatStepperModule} from "@angular/material/stepper";
import {MatTooltipModule} from "@angular/material/tooltip";
import {Observable, of, Subscription, tap} from "rxjs";
import {TramsDataSourceModel} from "src/app/payments/models/trams-data-source";
import {TramsDataSourceService} from "src/app/payments/service/trams-data-source.service";
import {BranchModel} from "src/app/shared/model/bank";
import {PayService} from "src/app/shared/service/pay/pay.service";
import {SharedModule} from "src/app/shared/shared.module";
import {TripService} from "src/app/trip/service/trip.service";
import {AutoUnsubscribe} from "src/infrastructure/decorators/auto-unsubscribe.decorator";
import {autocompleteAsyncValidator, autocompleteFormFilter, replayHttpQuery} from "src/infrastructure/helper/autocomplete-form-filter";
import clearFormFields from "src/infrastructure/helper/clear-form-fields";
import {numberPrevent} from "src/infrastructure/helper/number-input-preventer";
import {InvoiceTypeService} from "../../service/invoice-type.service";
import {InvoiceType} from "../../model/invoice-type";
import {CurrencyModel} from "src/app/shared/model/pay";
import {Trip} from "src/app/trip/model/trip";
import {AdxInvoice} from "src/app/adx-invoice/model/adx-invoice";
import {ConfirmationDialogInvoiceFormComponent} from "../confirmation-dialog-invoice-form/confirmation-dialog-invoice-form.component";

@AutoUnsubscribe()
@Component({
    selector: 'invoice-form-stepper',
    templateUrl: './invoice-form-stepper.component.html',
    styleUrl: './invoice-form-stepper.component.scss',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        SharedModule,
        MatInputModule,
        MatFormFieldModule,
        MatIconModule,
        MatStepperModule,
        MatButtonModule,
        MatAutocompleteModule,
        MatCardModule,
        MatRadioModule,
        MatSelectModule,
        MatTooltipModule,
        AsyncPipe,
    ]
})
export class InvoiceFormStepperComponent implements OnInit{
  @ViewChild('stepper') private stepperRef!: MatStepper;

  private dialogRef: MatDialogRef<InvoiceFormStepperComponent> = inject(MatDialogRef<InvoiceFormStepperComponent>);

  protected tripData = inject<Trip>(MAT_DIALOG_DATA);

  private formBuilder: FormBuilder = inject(FormBuilder);

  private tripService = inject(TripService);

  private tramsDataSourceService = inject(TramsDataSourceService);

  private payService = inject(PayService);

  private invoiceTypeService = inject(InvoiceTypeService);

  protected invoiceFormSearch!: FormGroup;

  protected invoiceFormCreate!: FormGroup;

  protected invoices$!: Observable<AdxInvoice[]>;
  protected filteredInvoicesOptions$!: Observable<AdxInvoice[]>;
  protected displayWithInvoice = (submitTo: AdxInvoice) => {
      return submitTo?.id ? `${submitTo?.id}` : '';
  };

  protected isNewSelection$?: Subscription;

  protected tramsDataSourceList$?: Observable<TramsDataSourceModel[]>;

  protected filteredBranch$?: Observable<BranchModel[]>;
  protected branchList$!: Observable<BranchModel[]>;
  protected displayWithBranch = (branch: BranchModel) => {
    return branch?.name || '';
  };

  protected filteredCurrency$?: Observable<CurrencyModel[]>;
  protected currencyList$!: Observable<CurrencyModel[]>;
  protected displayWithCurrencyList = (currency: CurrencyModel) => {
    return currency?.currencyName
      ? `${currency?.currencyName}${currency?.symbol ? ' (' + currency?.symbol + ')' : ''}`
      : '';
  };

  protected invoiceTypeList$?: Observable<InvoiceType[]>

  constructor(protected dialog: MatDialog) {
    this.invoiceFormSearch = this.formBuilder.group({
      tripId: [null, Validators.required],
      tripModel: [null, Validators.required],
      invoice: null,
      isNew: false,
      dataSourceId: null,
    });

    this.invoiceFormCreate = this.formBuilder.group({
      profile: [null, Validators.required],
      recordLocator: [null, [Validators.required, Validators.pattern(/^[a-zA-Z0-9]{6}$/)]],
      remarks: [null, Validators.required],
      amount: [null, Validators.required],
      branch: [null, Validators.required],
      invoiceType: [null, Validators.required],
      invoiceGroup: [null, Validators.required],
      currency: [null, Validators.required],
      dataSourceId: [null],
    });
  }

  ngOnInit(): void {
    if(this.tripData) {
      this.invoiceFormSearch.patchValue({
        tripId: this.tripData.id,
        tripModel: this.tripData,
      });
      
      this.invoices$ = of(this.tripData.adxInvoices);

      this.invoiceFormSearch.get('tripId')?.disable();
    }

    if (this.tripData && (this.tripData.dataSourceId == null)) {
      const tripId = this.tripData.id;
      if (tripId != null) {
        this.tripService.getTripById(tripId).pipe(tap((freshTrip: Trip) => {
          if (freshTrip?.dataSourceId != null) {
            this.tripData = { ...this.tripData, dataSourceId: freshTrip.dataSourceId } as Trip;
          }
        })).subscribe();
      }
    }

    this.tramsDataSourceList$ = this.tramsDataSourceService.getTramsDataSourceList();
    this.currencyList$ = replayHttpQuery(this.payService.getCurrency({ all: true }));
    this.invoiceTypeList$ = this.invoiceTypeService.getInvoicesType();

    this.setAutocompleteComponents();
    this.setCreateAutocompleteValidators();
    this.setIsNewFields(false);
    
    
    
    const hasInvoices = this.tripData.adxInvoices && this.tripData.adxInvoices.length > 0;
    const tripHasDataSource = this.tripData.dataSourceId != null;
    let dataSourceToSet: number | null = null;
    
    
    if (!tripHasDataSource && !hasInvoices) {
    } else if (!tripHasDataSource && hasInvoices) {
      dataSourceToSet = this.tripData.adxInvoices[0]?.dataSourceId;
    } else if (tripHasDataSource && !hasInvoices) {
      dataSourceToSet = this.tripData.dataSourceId;
    } else {
      dataSourceToSet = this.tripData.dataSourceId;
    }
    
    
    
    if (dataSourceToSet != null) {
      this.invoiceFormSearch.patchValue({dataSourceId: dataSourceToSet});
      this.invoiceFormSearch.get('dataSourceId')?.disable();
      
      this.branchList$ = replayHttpQuery(this.payService.getBranches({ tramsDataSourceId: dataSourceToSet }));
      
      const branchCtrl = this.invoiceFormCreate.controls['branch'];
      branchCtrl.patchValue(null);
      branchCtrl.setAsyncValidators(autocompleteAsyncValidator(this.branchList$));
      branchCtrl.updateValueAndValidity();
    } else {
      this.branchList$ = of([]);
    }

    
    if (!tripHasDataSource || !hasInvoices) {
      this.invoiceFormSearch.get('dataSourceId')?.valueChanges.subscribe((selectedDsId)=>{
        if (selectedDsId != null) {
          this.branchList$ = replayHttpQuery(this.payService.getBranches({ tramsDataSourceId: selectedDsId }));
          const branchCtrl = this.invoiceFormCreate.controls['branch'];
          branchCtrl.patchValue(null);
          branchCtrl.setAsyncValidators(autocompleteAsyncValidator(this.branchList$));
          branchCtrl.updateValueAndValidity();
        }
      });
    }

    this.isNewSelection$ = this.invoiceFormSearch.get('isNew')?.valueChanges.subscribe((value)=>{
      this.setIsNewFields(value);
    });

  }

  private setIsNewFields(isNew: boolean): void {
    const invoiceController = this.invoiceFormSearch.get('invoice');
    const dataSourceController = this.invoiceFormSearch.get('dataSourceId');
    const createDataSourceController = this.invoiceFormCreate.get('dataSourceId');

    invoiceController?.patchValue(null);
    
    createDataSourceController?.patchValue(null);

    if (this.hasNoInvoices()) {
      this.invoiceFormSearch.get('isNew')?.setValue(true, { emitEvent: false });
      isNew = true;
    }

    if(isNew) {
      invoiceController?.disable();
      invoiceController?.clearValidators();
      invoiceController?.clearAsyncValidators();

      if (this.tripData?.dataSourceId) {
        dataSourceController?.disable();
        dataSourceController?.patchValue(this.tripData.dataSourceId);
      } else {
        dataSourceController?.enable();
      }
      dataSourceController?.setValidators(Validators.required);

      createDataSourceController?.clearValidators();
    } else {
      invoiceController?.enable();
      invoiceController?.setValidators(Validators.required);
      invoiceController?.setAsyncValidators(autocompleteAsyncValidator(this.invoices$));

      dataSourceController?.disable();
      dataSourceController?.clearValidators();

      createDataSourceController?.setValidators(Validators.required);
    }

    invoiceController?.updateValueAndValidity();
    dataSourceController?.updateValueAndValidity();
    createDataSourceController?.updateValueAndValidity();
    this.setAutocompleteComponents();
  }

  private setCreateAutocompleteValidators(): void {
    this.invoiceFormCreate.controls['branch'].setAsyncValidators(autocompleteAsyncValidator(this.branchList$));

    this.invoiceFormCreate.controls['currency'].setAsyncValidators(autocompleteAsyncValidator(this.currencyList$));
  }

  private setAutocompleteComponents(): void {
    this.filteredInvoicesOptions$ = autocompleteFormFilter(
      this.invoiceFormSearch.controls['invoice'],
      'id',
      this.invoices$
    );

    this.filteredBranch$ = autocompleteFormFilter(
      this.invoiceFormCreate.controls['branch'],
      'name',
      this.branchList$
    );

    this.filteredCurrency$ = autocompleteFormFilter(
      this.invoiceFormCreate.controls['currency'],
      'currencyName',
      this.currencyList$
    );
  }

  protected nextInvoiceFormSelector(): void {
    if(this.invoiceFormSearch.get('tripModel')?.value){
      if(this.invoiceFormSearch.valid) {

        if(this.invoiceFormSearch.controls['isNew'].value === false) {
          const invoice = this.invoiceFormSearch.controls['tripModel'].value.adxInvoices.find(
            (invoice: AdxInvoice) => invoice.id === this.invoiceFormSearch.controls['invoice'].value.id
          );

          const profileControl = this.invoiceFormCreate.get('profile');
          const branchControl = this.invoiceFormCreate.get('branch');
          const dataSourceControl = this.invoiceFormCreate.get('dataSourceId');

          profileControl?.patchValue(invoice?.client);
          branchControl?.patchValue(invoice?.branch);
          dataSourceControl?.patchValue(invoice?.tramsDataSource?.id || invoice?.dataSourceId);


          profileControl?.disable();
          branchControl?.disable();
          dataSourceControl?.disable();
        }
      }

      this.invoiceFormCreate.patchValue({
        recordLocator: this.tripData.adxInvoices[0]?.recordLocator
      });

      this.invoiceFormCreate.get('recordLocator')?.disable();
      this.invoiceFormCreate.get('amount')?.disable();

      this.setAutocompleteComponents();
      this.stepperRef.next();
    } else {
      this.searchTrip();
    }
  }

  protected completeInvoiceFormCopy(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogInvoiceFormComponent, {
      data: {
      adxInvoiceId: this.invoiceFormSearch.value.invoice.id
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.dialogRef.close(true);
      }
    });
  }

  protected completeInvoiceFormCreate(): void {
    const formSearch = this.invoiceFormSearch.getRawValue();
    const formCreate = this.invoiceFormCreate.getRawValue();

    const dialogRef = this.dialog.open(ConfirmationDialogInvoiceFormComponent, {
      data: {
        clientId: formCreate.profile.id,
        tripId: formSearch.tripId,
        branchId: formCreate.branch.id,
        recordLocator: formCreate.recordLocator,
        invoiceTypeCode: formCreate.invoiceType,
        remarks: formCreate.remarks,
        invoiceGroup: formCreate.invoiceGroup,
        clientCentralDocumentId: formCreate.profile.tramsCentralDocumentId,
        totalAmount: formCreate.amount,
        currencyId: formCreate.currency.id,
        dataSourceId: formSearch.dataSourceId,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.dialogRef.close(true);
      }
    });
  }

  protected onInvoiceTypeChange(invoiceTypeCode: string): void {
    if (invoiceTypeCode === 'V') {
      this.invoiceFormCreate.get('amount')?.enable();
    }
    else{
      this.invoiceFormCreate.get('amount')?.disable();
      this.invoiceFormCreate.get('amount')?.reset();
    }
  }

  protected returnInvoiceFormCreate(): void {
    this.invoiceFormCreate.reset();
    this.invoiceFormCreate.enable();
  }

  protected closeModal(): void {
    this.dialogRef?.close()
  }

  protected searchTrip(): void {
    const tripId = this.invoiceFormSearch.value.tripId;
    if(tripId){
      this.tripService.getTripById(this.invoiceFormSearch.value.tripId).subscribe({
        next: (tripData)=>{
          this.invoiceFormSearch.get('tripId')?.disable();
          this.invoiceFormSearch.get('tripModel')?.setValue(tripData);
          this.invoices$ = of(tripData.adxInvoices);
          this.setAutocompleteComponents();
          this.setIsNewFields(this.invoiceFormSearch.get('isNew')?.value || false);
        },
        error: ()=>{
          this.invoiceFormSearch.get('tripId')?.setErrors({tripNotFound: true});
        }
      })
    } else {
      this.invoiceFormSearch.get('tripId')?.markAllAsTouched();

    }
  }

  protected clearSelectorFormFields(...args: string[]): void {
    const tripId = args.find((arg)=> arg === 'tripId');

    if(tripId) {
      this.invoiceFormSearch.get('tripId')?.enable();
      this.invoiceFormSearch.reset();
      return;
    }

    clearFormFields(this.invoiceFormSearch, ...args);
    this.setAutocompleteComponents();
  }

  protected clearCreateFormFields(...args: string[]): void {
    clearFormFields(this.invoiceFormCreate, ...args);
    this.setAutocompleteComponents();
  }

  protected preventFormValidation(event:any, stepper: MatStepper): void {
    stepper.selected!.interacted = false;
    if (event.selectedIndex === 0 && this.invoiceFormSearch.invalid) {
      event.preventDefault();
    }
  }

  protected checkInputAmount(event: KeyboardEvent): void {
    numberPrevent(event);
  }

  protected hasNoInvoices(): boolean {
    const tripModel = this.invoiceFormSearch.get('tripModel')?.value;
    return !tripModel?.adxInvoices || tripModel.adxInvoices.length === 0;
  }

  protected isPreselectedCreate(): boolean {
    return !this.invoiceFormSearch.controls['isNew'].value
  }

  protected hasTripDataSource(): boolean {
    const tripModel = this.invoiceFormSearch.get('tripModel')?.value;
    const hasDataSource = tripModel?.dataSourceId != null;
    return hasDataSource;
  }

  protected canCreateNewInvoice(): boolean {
    const hasDataSource = this.hasTripDataSource();
    const noInvoices = this.hasNoInvoices();
    const canCreate = hasDataSource || !noInvoices;
    return canCreate;
  }
}
